'use client';
import React, {useRef, useEffect} from 'react';
import {Button} from '@udemy-v2/react-core-components';
import {useI18n} from '@udemy-v2/i18n';
import {publishEvent} from '@udemy-v2/event-tracking';

import {PageForType} from '../../types/page';
import {getHeroCtaTexts} from './hero-cta.constants';
import {
    SubscriptionOfferPresented,
    SubscriptionLearnMoreEvent,
    LandingPageCTASelected,
} from '../events';
import {SubscriptionPlan} from '@/generated/graphql';

import styles from './hero-cta.module.scss';

interface HeroCtaProps {
    pageFor: PageForType;
    subscriptionPlans?: SubscriptionPlan[] | null;
    countryCode?: string | null;
}

function HeroCta({pageFor, subscriptionPlans, countryCode}: HeroCtaProps) {
    const {gettext} = useI18n();
    const {title, buttonText, url} = getHeroCtaTexts(gettext)[pageFor];
    const buttonRef = useRef<HTMLDivElement>(null);

    const uiRegion = 'top';

    const handleClick = () => {
        const firstPlan = subscriptionPlans?.[0];
        const firstPriceOption = firstPlan?.priceOptions?.[0];
        if (pageFor && pageFor === 'pp') {
            publishEvent(
                new SubscriptionLearnMoreEvent({
                    uiRegion,
                    planPriceId: firstPriceOption?.id || '0',
                    pageFor,
                }),
            );
        } else {
            publishEvent(
                new LandingPageCTASelected({
                    componentName: 'explore_now_' + uiRegion,
                    pageFor: pageFor,
                }),
            );
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // You can trigger an event here when the button becomes visible
                        // publishEvent(new ButtonVisibleEvent({uiRegion, countryCode: 'US'}));
                        observer.disconnect(); // Stop observing once visible

                        // Get the first subscription plan if available
                        const firstPlan = subscriptionPlans?.[0];
                        const firstPriceOption = firstPlan?.priceOptions?.[0];
                        const price = firstPriceOption?.price?.list?.toString() || '0';
                        const currency = firstPriceOption?.price?.currency?.toString() || 'null';
                        const hasTrial = !!firstPriceOption?.trial?.dateInterval;
                        const trialDays = firstPriceOption?.trial?.dateInterval?.count || 0;
                        const renewalIntervalType =
                            (firstPriceOption?.renewalInterval?.type?.toLowerCase() || 'month') as
                                | 'month'
                                | 'day'
                                | 'year'
                                | 'week';
                        const renewalIntervalCount =
                            Number(firstPriceOption?.renewalInterval?.count) || 0;

                        publishEvent(
                            new SubscriptionOfferPresented({
                                uiRegion: uiRegion,
                                primaryPricingOption: {
                                    price,
                                    listPrice: price,
                                    currency,
                                    renewalIntervalType,
                                    renewalIntervalCount,
                                    hasTrial,
                                    trialDays,
                                },
                                countryCode: countryCode || 'US',
                                pageFor,
                            }),
                        );
                    }
                });
            },
            {threshold: 0.5}, // Trigger when at least 50% of the button is visible
        );

        if (buttonRef.current) {
            observer.observe(buttonRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [countryCode, subscriptionPlans, pageFor]);

    return (
        <div className={styles.heroCtaLayout}>
            <section className={styles.heroCtaContainer}>
                <p className={styles.heroCtaText}>{title}</p>
                <div ref={buttonRef}>
                    <Button
                        onClick={handleClick}
                        udStyle="primary"
                        size="large"
                        className={styles.hero_cta_button}
                    >
                        {buttonText}
                    </Button>
                </div>
            </section>
        </div>
    );
}

export {HeroCta};
